import type {
  ReportDashboardMetricOptions,
  ReportResourceType,
  Data,
  Dimension,
  DatasetBase,
  ReportResultsMeta,
  ValueRow,
  ReportResults,
} from '@core/reports-types';
import { isKnownResourceType, InitialDataChecksumToken, OTHER_GROUP_ID } from '@core/reports-types';
import { deepClone } from '@core/utils-legacy';

const emptyDataset = () => ({
  dimensions: [],
  source: [],
});

export const createDataset = (data: ReportResults, metric: ReportDashboardMetricOptions) => {
  const { columns, rows } = data.results;

  const normalizedRows = rows.reduce(
    (acc, row) => {
      const { values, ...rowInfo } = row;
      const valueRow: ValueRow<number> = [rowInfo, ...values];
      acc.push(valueRow);
      return acc;
    },
    <Array<ValueRow<number>>>[],
  );

  return new Dataset(deepClone([columns, ...normalizedRows]), metric, data.checksum, data.meta);
};

// TODO visualizations. Dataset is no longer editable. It should be immutable.
class Dataset implements DatasetBase {
  public readonly datasetSize: {
    cols: number;
    rows: number;
  };

  public isSingleDimension: boolean;
  public dimensions: Dimension[];
  public isInitial: boolean;

  public dataset: DatasetBase['dataset'];

  public resourceType?: ReportResourceType;
  public isEmpty: boolean;
  public isSingleValue: boolean;

  public categoriesMap: Record<string, string> = {};
  public dimensionsMap: Record<string, string> = {};

  constructor(
    private readonly raw: Data,
    public readonly metric: ReportDashboardMetricOptions,
    public readonly checksum: string | undefined,
    public readonly meta?: ReportResultsMeta,
    public readonly parent?: DatasetBase,
  ) {
    this.datasetSize = { cols: raw[0].length, rows: raw.length };
    this.seal();
  }

  public getDataAt(target: [number, number]) {
    return this.raw[target[0]][target[1]];
  }

  public getRow(rowIndex: number) {
    return this.raw[rowIndex];
  }

  public getRawData() {
    return this.raw;
  }

  public calcTotals() {
    return this.dataset.source.reduce(
      (acc, row) => {
        const values = <number[]>row.slice(1);
        acc.push([
          <string>row[0],
          values.reduce((rowAcc: number, value: number) => <number>rowAcc + <number>value, 0),
        ]);
        return acc;
      },
      <Array<[string, number]>>[],
    );
  }

  public getCategoryName(index: number) {
    const row = this.raw[index + 1];
    return row ? row[0].name : '';
  }

  public getDimensionNameById(id: string) {
    return this.dimensionsMap[id] || id;
  }

  public getCategoryNameById(id: string) {
    return this.categoriesMap[id] || id;
  }

  public getIndexDimension() {
    return this.raw[0][0];
  }

  public truncateDataset(dimension: 'first', length: number): DatasetBase;
  public truncateDataset(dimension: 'second', length: number, groupIntoOther: false): DatasetBase;
  public truncateDataset(
    dimension: 'second',
    length: number,
    groupIntoOther: true,
    otherLabel: string,
  ): DatasetBase;
  public truncateDataset(
    dimension: 'first' | 'second',
    length: number,
    groupIntoOther?: boolean,
    otherLabel?: string,
  ): DatasetBase {
    const raw = this.raw;
    const [header, ...values] = raw;

    if (dimension === 'first') {
      return new Dataset(
        [header, ...values.slice(0, length)],
        this.metric,
        this.checksum,
        this.meta,
        this,
      );
    }

    // First element is the dimension, hence <=
    if (header.length <= length + 1) {
      return this;
    }
    // we want the other group to always have at least 2 elements
    const adjustedLength = length - 1;
    const hd = [header[0], ...header.slice(1, adjustedLength + 1)];
    const vls = values.map((row): ValueRow<number> => {
      const [dimension, ...values] = row;
      const truncated = values.slice(0, adjustedLength);
      const rest = values.slice(adjustedLength);
      if (groupIntoOther) {
        truncated.push(rest.reduce((acc, v) => acc + v, 0));
      }
      return [dimension, ...truncated];
    });
    if (groupIntoOther) {
      hd.push({ name: otherLabel!, id: OTHER_GROUP_ID });
    }

    return new Dataset([hd, ...vls], this.metric, this.checksum, this.meta, this);
  }

  public filterDataset(fn: (x: ValueRow<any>) => boolean): DatasetBase {
    const [headerRow, ...valueRows] = this.raw;
    const filtered = valueRows.filter(fn);
    return new Dataset([headerRow, ...filtered], this.metric, this.checksum, this.meta, this);
  }

  protected buildDataset(data: Data): DatasetBase['dataset'] {
    const rows = data.slice(1);

    return {
      dimensions: data[0].map(d => {
        // name is sometimes not even displayed, but echarts breaks when it's missing
        const name = d.name && d.name.length ? d.name : d.id || 'missing name';
        return {
          name: `${name}`,
          id: d.id || d.name,
        };
      }),
      source: <DatasetBase['dataset']['source']>(
        rows.map(row => [row[0].id || row[0].name, ...row.slice(1)])
      ),
    };
  }

  private seal() {
    const data = this.raw;
    const dimensions = data[0].slice(1);
    const dimensionsLength = dimensions.length;

    this.resourceType = isKnownResourceType(this.metric.resourceId)
      ? this.metric.resourceId
      : undefined;

    this.dataset = data[1] && data[1].length ? this.buildDataset(data) : emptyDataset();
    this.categoriesMap = data
      .slice(1)
      .filter(row => row.length)
      .reduce((acc, r) => {
        acc[r[0].id] = r[0].name;
        return acc;
      }, this.categoriesMap);

    this.dimensionsMap = data[0].reduce((acc, dim) => {
      acc[dim.id] = dim.name;
      return acc;
    }, this.dimensionsMap);

    this.dimensions = this.dataset.dimensions || [];
    this.isSingleDimension = dimensionsLength === 1;
    this.isSingleValue = this.datasetSize.cols === 0;
    this.isEmpty = this.dataset.source.length === 0;
    this.isInitial = this.checksum === InitialDataChecksumToken;
  }
}
